import * as THREE from 'three';
import { SKIN_VOLUME } from './volume-skin.js';

/** Re-seat authored ink on the connected skin and inherit its barycentric bone weights. */
export function createSkinInkProjector(skin: THREE.BufferGeometry) {
  const position = skin.getAttribute('position'), normals = skin.getAttribute('normal'), bones = skin.getAttribute('skinIndex'), weights = skin.getAttribute('skinWeight'), index = skin.index!;
  const points = Array.from({ length: position.count }, (_, i) => new THREE.Vector3().fromBufferAttribute(position, i));
  const buckets = new Map<number, number[]>(), bounds: THREE.Box3[] = [], size = .04;
  for (let t = 0; t < index.count; t += 3) {
    const box = new THREE.Box3().setFromPoints([points[index.getX(t)], points[index.getX(t + 1)], points[index.getX(t + 2)]]); bounds.push(box);
    for (let band = Math.floor(box.min.y / size); band <= Math.floor(box.max.y / size); band++) {
      const list = buckets.get(band) ?? []; list.push(t / 3); buckets.set(band, list);
    }
  }
  const triangle = new THREE.Triangle(), point = new THREE.Vector3(), nearest = new THREE.Vector3(), normal = new THREE.Vector3(), barycentric = new THREE.Vector3();
  return (ink: THREE.BufferGeometry, offset = .0009, place = true) => {
    const inkPosition = ink.getAttribute('position'), inkNormal = ink.getAttribute('normal');
    const skinIndices = new Uint16Array(inkPosition.count * 4), skinWeights = new Float32Array(inkPosition.count * 4);
    const volume = skin.getAttribute(SKIN_VOLUME), inkVolume = new Float32Array(inkPosition.count);
    for (let v = 0; v < inkPosition.count; v++) {
      point.fromBufferAttribute(inkPosition, v);
      let best = Infinity, face = -1;
      const band = Math.floor(point.y / size);
      for (let b = band - 1; b <= band + 1; b++) for (const t of buckets.get(b) ?? []) {
        if (bounds[t].distanceToPoint(point) ** 2 >= best) continue;
        triangle.set(points[index.getX(t * 3)], points[index.getX(t * 3 + 1)], points[index.getX(t * 3 + 2)]);
        triangle.closestPointToPoint(point, nearest);
        const distance = nearest.distanceToSquared(point);
        if (distance < best) { best = distance; face = t; }
      }
      if (face < 0) throw new Error('Ink projection could not find the body skin');
      const ids = [index.getX(face * 3), index.getX(face * 3 + 1), index.getX(face * 3 + 2)];
      triangle.set(points[ids[0]], points[ids[1]], points[ids[2]]); triangle.closestPointToPoint(point, nearest); triangle.getBarycoord(nearest, barycentric);
      const amounts = new Map<number, number>(); normal.set(0, 0, 0);
      for (let c = 0; c < 3; c++) {
        const fraction = barycentric.getComponent(c), id = ids[c];
        if (volume) inkVolume[v] += volume.getX(id) * fraction;
        normal.addScaledVector(new THREE.Vector3().fromBufferAttribute(normals, id), fraction);
        for (let j = 0; j < 4; j++) {
          const bone = bones.getComponent(id, j), weight = weights.getComponent(id, j) * fraction;
          amounts.set(bone, (amounts.get(bone) ?? 0) + weight);
        }
      }
      if (place) {
        normal.normalize(); inkNormal.setXYZ(v, normal.x, normal.y, normal.z);
        triangle.getNormal(normal); nearest.addScaledVector(normal, offset); inkPosition.setXYZ(v, nearest.x, nearest.y, nearest.z);
      }
      const influence = [...amounts].filter(([, amount]) => amount > 1e-8).sort((a, b) => b[1] - a[1]).slice(0, 4), sum = influence.reduce((n, [, amount]) => n + amount, 0);
      for (let j = 0; j < influence.length; j++) { skinIndices[v * 4 + j] = influence[j][0]; skinWeights[v * 4 + j] = influence[j][1] / sum; }
    }
    ink.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndices, 4)); ink.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeights, 4));
    if (volume) ink.setAttribute(SKIN_VOLUME, new THREE.Float32BufferAttribute(inkVolume, 1));
  };
}
